import { useEffect, useRef, useState } from "react";
import { X, Mail, ArrowLeft } from "lucide-react";
import { deviceRequest } from "./device-session";
import { initializeAnalytics } from "../portal/analytics";
import { registrationMetaContext, trackRegistration } from "../portal/meta";

export type LoginReason = "login" | "trial" | "save" | "purchase";
export type LoginResult = { newRegistration: boolean; registration: { id: string; eventTime: number; sessionId: string } | null };
const reasons = {
  login: ["注册 / 登录", "使用邮箱验证码，新用户自动注册，已有账户直接登录。"],
  trial: ["喜欢这个故事？", "免费注册，保存追剧进度。下次登录还能接着看。"],
  save: ["免费注册，收藏好故事", "登录后收藏到你的账户，以后可以重新登录找回。"],
  purchase: ["先登录，再购买", "购买记录将保存到你的账户，重新登录即可找回。"],
};
export default function EmailLogin({ reason, onClose, onSuccess }: { reason: LoginReason; onClose: () => void; onSuccess: (result: LoginResult) => Promise<void> }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState<boolean | null>(null);
  const [wait, setWait] = useState(0);
  useEffect(() => { dialog.current?.showModal(); const old = document.body.style.overflow; document.body.style.overflow = "hidden";
    void deviceRequest<{ emailAvailable: boolean }>("account/config").then(v => setAvailable(v.emailAvailable)).catch(() => setError("暂时无法连接，请稍后重试。"));
    return () => { dialog.current?.close(); document.body.style.overflow = old; }; }, []);
  useEffect(() => { if (!wait) return; const timer = setTimeout(() => setWait(v => Math.max(0, v - 1)), 1000); return () => clearTimeout(timer); }, [wait]);
  async function send() {
    if (busy || wait > 0) return;
    setBusy(true); setError("");
    try { const result = await deviceRequest<{ challengeId: string; resendAfter: number }>("account/email/code", "POST", { email: email.trim() }); setChallenge(result.challengeId); setCode(""); setWait(result.resendAfter); }
    catch (e) { setError(e instanceof Error ? e.message : "验证码未能发出，请稍后重试。"); }
    finally { setBusy(false); }
  }
  async function verify() {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const session = await initializeAnalytics();
      const result = await deviceRequest<LoginResult>("account/email/verify", "POST", { challengeId: challenge, code, sessionId: session?.sessionId || "", meta: registrationMetaContext() });
      trackRegistration(result.registration);
      await onSuccess(result);
    } catch (e) { setError(e instanceof Error ? e.message : "暂时无法登录，请重试。"); setBusy(false); }
  }
  return <dialog ref={dialog} className="email-login-dialog" aria-labelledby="email-login-title" onCancel={e => { e.preventDefault(); if (!busy) onClose(); }}>
    <button className="email-login-close" type="button" aria-label="关闭注册登录" disabled={busy} onClick={onClose}><X size={22} /></button>
    <span className="email-login-icon"><Mail size={26} /></span>
    <p className="account-eyebrow">Yestv 账户</p>
    <h2 id="email-login-title">{reasons[reason][0]}</h2>
    <p>{reasons[reason][1]}</p>
    {available === false && <p className="email-login-warning" role="status">验证码邮件暂不可用。你仍可浏览和试看，请稍后再来注册或登录。</p>}
    <form onSubmit={e => { e.preventDefault(); void (challenge ? verify() : send()); }}>
      <label>邮箱地址<input type="email" autoComplete="email" inputMode="email" required maxLength={254} value={email} disabled={busy || !!challenge} onChange={e => setEmail(e.target.value)} autoFocus /></label>
      {challenge && <><p className="email-code-sent">验证码已发送，请检查收件箱和垃圾邮件。10 分钟内有效。</p>
        <label>6 位验证码<input autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" minLength={6} maxLength={6} required value={code} disabled={busy} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} /></label>
        <div className="email-code-actions"><button type="button" disabled={busy} onClick={() => { setChallenge(""); setCode(""); setError(""); }}><ArrowLeft size={14} />修改邮箱</button><button type="button" disabled={busy || wait > 0} onClick={() => void send()}>{wait ? `${wait} 秒后可重发` : "重新发送验证码"}</button></div>
      </>}
      {error && <p className="email-login-error" role="alert">{error}</p>}
      <button className="account-primary" disabled={busy || available !== true || (!challenge && wait > 0)}>{busy ? "处理中…" : challenge ? "验证并继续" : wait > 0 ? `${wait} 秒后可发送` : "发送验证码"}</button>
    </form>
    <p className="email-login-note">无需设置密码。同一账户仅保留一个有效登录端；重新登录后，原登录端将退出。</p>
    <button className="email-login-later" disabled={busy} onClick={onClose}>{reason === "trial" ? "稍后注册，继续试看" : "暂不登录，返回浏览"}</button>
  </dialog>;
}
